/**
 * The Command Desk's two cards panels, derived - RUN 86.
 *
 * Spec: `01-command-desk.md` Sec. 8 and 8a (Cards Listing: every carded player, both
 * competitions, **sorted by red, then yellow, then green** - TMS sorts by country, the Technical
 * Delegate wants the ranking) and Sec. 9 (Cards Average Per Game: `Green 3.2 - Yellow 1.4 - Red
 * 0.1`, one decimal, both competitions, with a per-game trend). The source is `GET /cards`: the
 * TMS public cards reports as the server last read them.
 *
 * **What "per game" means here.** Sec. 9 says both competitions and one decimal, and is silent on
 * the denominator. It is the reports' own: each report says `after M games played`, so a colour's
 * average is the cards of that colour in both reports over the games both report played - the
 * same basis TMS counts on, never a count of fixtures this build guesses were played. Where only
 * one competition's report has been read, the figures are that competition's and the panel says
 * so; where no game has been played there is no average, and the panel says that instead of 0.0.
 *
 * **Times are the tournament's.** A report's `As Of` is the last time the server read it from TMS
 * and found it good (`fetchedAt`) - spec 08 Sec. 6's "true time of the last successful fetch".
 * Since the fix round a read that finds the report saying what it said before moves only that
 * time, so the stamp stays current while the cards do not change; the minute TMS generated the
 * report it holds is on the hover. Both are shown on the tournament clock (`pushTime`, the
 * Asia/Tokyo reader every receipt uses), with the date when it is not today's, and the whole
 * instant on hover (`exactTournamentInstant`).
 *
 * Pure functions, so that every rule above is a unit test and not a rendering accident.
 */

import type {
  CardCompetitionBody,
  CardCountsBody,
  CardsBody,
} from "../../api/types.js";
import { tournamentDay } from "../../app/tournamentTime.js";
import { exactTournamentInstant, shortTournamentTime } from "../appointments/vocabulary.js";
import { pushTime } from "../marking/PushReceiptNote.js";

/** `Men's` / `Women's` - how the Desk names a competition everywhere else. */
export function competitionLabel(gender: "m" | "w"): string {
  return gender === "m" ? "Men's" : "Women's";
}

/* -- Sec. 8 - the listing ---------------------------------------------------------------- */

export interface CardsListingRow {
  /** Unique across both competitions: the same team code plays in both. */
  key: string;
  competitionId: string;
  gender: "m" | "w";
  teamCode: string;
  shirtNumber: number;
  playerName: string;
  red: number;
  yellow: number;
  green: number;
}

/**
 * Every carded player in both reports, ranked: most reds first, then most yellows, then most
 * greens (Sec. 8). Ties fall back to the report's own order - team, then shirt - with the Men's
 * competition first, so the list never reshuffles between two identical reads. A row with no card
 * at all is not a carded player and is left out.
 */
export function cardsListing(body: CardsBody): CardsListingRow[] {
  const rows: CardsListingRow[] = [];
  for (const competition of body.competitions) {
    for (const player of competition.players) {
      if (player.red + player.yellow + player.green === 0) continue;
      rows.push({
        key: `${competition.competitionId}-${player.teamCode}-${String(player.shirtNumber)}`,
        competitionId: competition.competitionId,
        gender: competition.gender,
        teamCode: player.teamCode,
        shirtNumber: player.shirtNumber,
        playerName: player.playerName,
        red: player.red,
        yellow: player.yellow,
        green: player.green,
      });
    }
  }
  return rows.sort(
    (a, b) =>
      b.red - a.red ||
      b.yellow - a.yellow ||
      b.green - a.green ||
      a.competitionId.localeCompare(b.competitionId) ||
      a.teamCode.localeCompare(b.teamCode) ||
      a.shirtNumber - b.shirtNumber,
  );
}

/* -- Sec. 9 - the averages --------------------------------------------------------------- */

export interface PerGame {
  red: number;
  yellow: number;
  green: number;
  total: number;
}

export interface CompetitionAverage {
  competitionId: string;
  gender: "m" | "w";
  games: number;
  cards: CardCountsBody;
  /** Null when this report says no game has been played. */
  perGame: PerGame | null;
}

export interface CardsAverages {
  /** Games played, summed over the reports held. */
  games: number;
  cards: CardCountsBody & { total: number };
  /** Null when no game has been played in any report held - there is no average to state. */
  perGame: PerGame | null;
  /** The competitions whose report is held, in TMS order. */
  competitions: CompetitionAverage[];
  /** The competitions with no report yet, so the panel can say whose figures these are not. */
  missing: ("m" | "w")[];
}

function perGameOf(cards: CardCountsBody, games: number): PerGame | null {
  if (games <= 0) return null;
  const total = cards.red + cards.yellow + cards.green;
  return {
    red: cards.red / games,
    yellow: cards.yellow / games,
    green: cards.green / games,
    total: total / games,
  };
}

/** Sec. 9's figures, or null when neither competition's report has been read. */
export function cardsAverages(body: CardsBody): CardsAverages | null {
  const held = body.competitions.filter(
    (competition): competition is CardCompetitionBody & { report: NonNullable<CardCompetitionBody["report"]> } =>
      competition.report !== null,
  );
  if (held.length === 0) return null;
  const cards = { red: 0, yellow: 0, green: 0 };
  let games = 0;
  const competitions = held.map((competition) => {
    const totals = competition.report.totals;
    cards.red += totals.red;
    cards.yellow += totals.yellow;
    cards.green += totals.green;
    games += competition.report.gamesPlayed;
    return {
      competitionId: competition.competitionId,
      gender: competition.gender,
      games: competition.report.gamesPlayed,
      cards: { ...totals },
      perGame: perGameOf(totals, competition.report.gamesPlayed),
    };
  });
  return {
    games,
    cards: { ...cards, total: cards.red + cards.yellow + cards.green },
    perGame: perGameOf(cards, games),
    competitions,
    missing: body.competitions
      .filter((competition) => competition.report === null)
      .map((competition) => competition.gender),
  };
}

/**
 * One decimal, as Sec. 9 prints it - rounding a half UP, as a person would: 3 cards in 20 games is
 * 0.15, which binary floating point holds as 0.1499... and `toFixed` alone would print as 0.1.
 */
export function oneDecimal(value: number): string {
  return (Math.round((value + Number.EPSILON) * 10) / 10).toFixed(1);
}

/**
 * Sec. 9's trend: the combined average per game after each report the server has stored, oldest
 * first. The two competitions report on their own schedules, so at every point each one's latest
 * figures up to that moment are carried forward and added together - and the line starts only
 * once BOTH competitions have reported (every competition the Desk shows, whether or not it has
 * a point yet), so it never joins a one-competition figure to a two-competition one, and never
 * draws one competition's line as if it were both. Points with no game yet are left out. Fewer
 * than two points draw no line.
 */
export function cardsTrend(body: CardsBody): PerGame[] {
  const covered = body.competitions.length;
  // A point whose time does not read is ordered by its games alone - never by a NaN, which would
  // leave the sort with no consistent order (follow-up round 2).
  const events = body.competitions
    .flatMap((competition) =>
      competition.trend.map((point) => ({ competitionId: competition.competitionId, point })),
    )
    .sort((a, b) => {
      const [first, second] = [Date.parse(a.point.generatedAt), Date.parse(b.point.generatedAt)];
      const byTime = Number.isNaN(first) || Number.isNaN(second) ? 0 : first - second;
      return byTime || a.point.gamesPlayed - b.point.gamesPlayed;
    });
  const latest = new Map<string, (typeof events)[number]["point"]>();
  const series: PerGame[] = [];
  for (const event of events) {
    latest.set(event.competitionId, event.point);
    if (latest.size < covered) continue;
    const sum = { red: 0, yellow: 0, green: 0 };
    let games = 0;
    for (const point of latest.values()) {
      sum.red += point.red;
      sum.yellow += point.yellow;
      sum.green += point.green;
      games += point.gamesPlayed;
    }
    const average = perGameOf(sum, games);
    if (average !== null) series.push(average);
  }
  return series;
}

/**
 * A polyline's `points` for a sparkline in a 100 x 30 box, a value of `max` at the top and 0 at
 * the bottom, two units of margin all round.
 */
export function sparkPoints(values: readonly number[], max: number): string {
  if (values.length < 2 || max <= 0) return "";
  const step = 96 / (values.length - 1);
  return values
    .map((value, index) => {
      const x = 2 + index * step;
      const y = 28 - (Math.min(Math.max(value, 0), max) / max) * 26;
      return `${String(Number(x.toFixed(2)))},${String(Number(y.toFixed(2)))}`;
    })
    .join(" ");
}

/* -- When, and what went wrong ----------------------------------------------------------- */

export interface ReportStamp {
  /** `As Of 18:12` - with the date, `As Of 17 Sep 18:12`, when it is not today's. */
  label: string;
  /** The whole instant, for the hover: `18 Sep 2026, 18:12:00 Tokyo time`. */
  title: string;
}

/**
 * A report's `As Of`: the last time the server read it from TMS and found it good (`fetchedAt`,
 * spec 08 Sec. 6), and on hover that time in full with the minute TMS generated it.
 */
export function reportAsOf(
  report: { fetchedAt: string; generatedAt: string },
  now: Date,
): ReportStamp {
  const read = reportStamp(report.fetchedAt, now);
  const fetched = new Date(report.fetchedAt);
  const generated = new Date(report.generatedAt);
  // Each instant is formatted only when it reads; one that does not is printed as it came, or left
  // out - never thrown on (follow-up round 2).
  return {
    label: read.label,
    title:
      (Number.isNaN(fetched.getTime())
        ? `Read from TMS ${String(report.fetchedAt)}`
        : `Read from TMS ${exactTournamentInstant(report.fetchedAt)}`) +
      (Number.isNaN(generated.getTime())
        ? ""
        : `; TMS generated this report ${exactTournamentInstant(report.generatedAt)}`),
  };
}

/**
 * The SERVER's clock, now: the `servedAt` it sent with an answer, advanced by the device's
 * monotonic time since that answer arrived (follow-up round, item 2b). The house rule is one clock
 * per comparison, and a report's `fetchedAt` is on the server's clock - so its age is measured on
 * the server's clock too, never against the device's wall clock, which can be a few seconds or a
 * few hours out. The monotonic part cannot jump when the device's clock is set.
 */
export function servedClock(servedAt: string, sinceArrivalMs: number): Date {
  // An answer with no `servedAt`, or one that does not read as an instant, is not thrown on - the
  // house rule (`PushReceiptNote.tsx`): the Desk falls back to its own clock for that answer. An
  // Invalid Date here reached `formatToParts`, which throws, and with no error boundary in this
  // app the whole Control Centre unmounted over one field (follow-up round 2).
  const base = Date.parse(servedAt);
  const since = Number.isFinite(sinceArrivalMs) ? Math.max(0, sinceArrivalMs) : 0;
  return Number.isNaN(base) ? new Date() : new Date(base + since);
}

/**
 * Whether a report's `As Of` is older than it should be (fix round 3, item 6b; spec 08 Sec. 6:
 * the stamp "turns amber past the configured threshold"): the server's reader is on, and its last
 * good read of the report is more than twice its interval, plus a minute, ago - two reads missed.
 * With the reader off the stamp is as old as it says, and the panel says the reader is off.
 *
 * `serverNow` must be the server's clock ({@link servedClock}) - the one `fetchedAt` is on.
 */
export function reportIsStale(
  report: { fetchedAt: string },
  body: Pick<CardsBody, "poller" | "intervalMinutes">,
  serverNow: Date,
): boolean {
  if (body.poller !== "on") return false;
  const read = new Date(report.fetchedAt).getTime();
  if (Number.isNaN(read)) return false;
  return serverNow.getTime() - read > (2 * body.intervalMinutes + 1) * 60_000;
}

/** The one short line that says why the server's reader has paused, or null when it has not. */
export function pauseLine(pause: CardsBody["pause"]): string | null {
  switch (pause) {
    case "ceiling":
      return "Reading is paused: the server has made today's ceiling of requests to TMS, and reads again after midnight, Tokyo time.";
    case "outside-match-days":
      return "Reading is paused: today is not one of the Games' match days.";
    case "no-fixtures":
      return "Reading is paused: no fixtures are loaded, so the Games' match days are unknown.";
    default:
      return null;
  }
}

/** An instant as an `As Of` stamp on the tournament's clock. */
export function reportStamp(generatedAt: string, now: Date): ReportStamp {
  const at = new Date(generatedAt);
  // An unreadable instant is printed, never thrown on (`PushReceiptNote.tsx`) - the stamp's own,
  // and `now`, which is only compared when it is a real instant: `tournamentDay` formats with
  // `formatToParts`, and that throws on an Invalid Date.
  if (Number.isNaN(at.getTime())) return { label: `As Of ${String(generatedAt)}`, title: String(generatedAt) };
  const sameDay = !Number.isNaN(now.getTime()) && tournamentDay(at) === tournamentDay(now);
  return {
    label: `As Of ${sameDay ? pushTime(generatedAt) : shortTournamentTime(generatedAt)}`,
    title: `Generated by TMS ${exactTournamentInstant(generatedAt)}`,
  };
}

/** An ISO instant inside a sentence the server wrote - `2026-09-18T09:12:00.000Z`. */
const INSTANT = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})/g;

/**
 * A reason the server stored, with every instant in it moved onto the tournament's clock: the
 * server writes instants in UTC (`the report was generated at 2026-09-18T09:12:00.000Z, before
 * ...`), and the Desk never shows a time on any clock but Tokyo's.
 */
export function onTournamentClock(reason: string): string {
  return reason.replace(INSTANT, (at) => shortTournamentTime(at));
}

/**
 * What a competition's latest read says went wrong, in one sentence - or null when it did not.
 * The report still held, if any, is named, because it is what the panel is still showing.
 */
export function readProblem(competition: CardCompetitionBody, now: Date): string | null {
  const read = competition.lastRead;
  if (read === null || (read.outcome !== "refused" && read.outcome !== "fetch_failed")) return null;
  const who = competitionLabel(competition.gender);
  const when = reportStamp(read.at, now).label.replace(/^As Of /, "");
  const why = onTournamentClock(read.reason ?? "no reason was recorded");
  const verb = read.outcome === "fetch_failed" ? "could not be fetched" : "could not be read";
  const still =
    competition.report === null
      ? " No earlier report is held."
      : ` Still showing the report ${reportAsOf(competition.report, now).label.replace(/^As Of/, "as of")}.`;
  return `The latest ${who} report (${when}) ${verb}: ${why}.${still}`;
}

/** `every five minutes`, `every minute`, `every 12 minutes` - how the server's interval is said. */
export function everyMinutes(minutes: number): string {
  const words = ["", "", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
  if (minutes === 1) return "every minute";
  const said = words[minutes];
  return `every ${said === undefined || said === "" ? String(minutes) : said} minutes`;
}

/**
 * The one sentence a screen reader is told about the cards reads: which competitions' latest
 * reads failed, and how - and nothing that changes on every read. No time is in it, so it is
 * announced when the set of failures changes, and not again every five minutes while it holds.
 */
export function cardsAnnouncement(body: CardsBody): string {
  return body.competitions
    .flatMap((competition) => {
      const read = competition.lastRead;
      if (read === null) return [];
      const who = competitionLabel(competition.gender);
      if (read.outcome === "fetch_failed") return [`The latest ${who} cards report could not be fetched.`];
      if (read.outcome === "refused") return [`The latest ${who} cards report could not be read.`];
      return [];
    })
    .join(" ");
}
